import { useEffect, useState } from 'react'

/**
 * Animates a number from 0 → `target` over `durationMs`, with an
 * optional pre-start `delayMs`. Cleans up on unmount and re-runs if
 * any input changes.
 *
 * Drives the win-pill / total-win-banner count-ups — the small piece
 * of polish that turns a number-pop into a "you won" moment.
 */
export function useCountUp(target: number, durationMs = 700, delayMs = 0): number {
  const [value, setValue] = useState(0)

  useEffect(() => {
    if (!Number.isFinite(target) || target <= 0) {
      setValue(target)
      return
    }

    let raf = 0
    let startedAt: number | null = null

    const tick = (now: number) => {
      if (startedAt === null) startedAt = now
      const elapsed = now - startedAt
      const progress = Math.min(1, elapsed / durationMs)
      // ease-out cubic — fast start, gentle settle
      const eased = 1 - Math.pow(1 - progress, 3)
      setValue(target * eased)
      if (progress < 1) {
        raf = requestAnimationFrame(tick)
      }
    }

    const startTimer = setTimeout(() => {
      raf = requestAnimationFrame(tick)
    }, delayMs)

    return () => {
      clearTimeout(startTimer)
      if (raf) cancelAnimationFrame(raf)
    }
  }, [target, durationMs, delayMs])

  return value
}
